import { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";

interface TagPickerFieldProps {
  label: string;
  helper: string;
  suggestedTags: string[];
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  maxTags?: number;
  conflictTags?: string[];
  conflictLabel?: string;
}

const TagPickerField = ({
  label,
  helper,
  suggestedTags,
  selectedTags,
  onTagsChange,
  maxTags = 5,
  conflictTags = [],
  conflictLabel,
}: TagPickerFieldProps) => {
  const [customInput, setCustomInput] = useState("");
  const [conflictWarning, setConflictWarning] = useState<string | null>(null);
  const [wordWarning, setWordWarning] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const wordWarningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const atLimit = selectedTags.length >= maxTags;

  // Clear word warning timer on unmount
  useEffect(() => {
    return () => {
      if (wordWarningTimer.current) clearTimeout(wordWarningTimer.current);
    };
  }, []);

  const toggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      onTagsChange(selectedTags.filter((t) => t !== tag));
    } else if (!atLimit) {
      onTagsChange([...selectedTags, tag]);
    }
  };

  const addCustomTag = () => {
    const trimmed = customInput.trim().slice(0, 20);
    if (!trimmed || atLimit) return;
    if (selectedTags.some((t) => t.toLowerCase() === trimmed.toLowerCase())) {
      setCustomInput("");
      return;
    }
    if (conflictTags.some((t) => t.toLowerCase() === trimmed.toLowerCase())) {
      setConflictWarning(trimmed);
    } else {
      setConflictWarning(null);
    }
    onTagsChange([...selectedTags, trimmed]);
    setCustomInput("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Enforce 20 char limit
    if (value.length > 20) {
      value = value.slice(0, 20);
    }

    // Enforce 2 word max: block space if already has 2 words
    const words = value.trimStart().split(/\s+/).filter(Boolean);
    if (words.length > 2) {
      // Don't allow the third word
      showWordWarning();
      return;
    }
    // If exactly 2 words and user just typed a trailing space, block it
    if (words.length === 2 && value.endsWith(" ") && value.trim().split(/\s+/).length >= 2) {
      // Allow the space between word1 and word2, but not after word2
      const trimmed = value.trimStart();
      const spaceCount = (trimmed.match(/\s+/g) || []).length;
      if (spaceCount > 1) {
        showWordWarning();
        return;
      }
    }

    setCustomInput(value);
  };

  const showWordWarning = () => {
    setWordWarning(true);
    if (wordWarningTimer.current) clearTimeout(wordWarningTimer.current);
    wordWarningTimer.current = setTimeout(() => setWordWarning(false), 2000);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addCustomTag();
    }
    // Block space if already at 2 words
    if (e.key === " ") {
      const words = customInput.trimStart().split(/\s+/).filter(Boolean);
      if (words.length >= 2) {
        e.preventDefault();
        showWordWarning();
      }
    }
  };

  const scrollRowStyle: React.CSSProperties = {
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    WebkitOverflowScrolling: "touch",
  };

  return (
    <div>
      <p className="text-sm font-bold text-foreground">{label}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{helper}</p>

      {/* Selected tags — single horizontal scroll row */}
      {selectedTags.length > 0 && (
        <div
          className="mt-2 flex flex-nowrap gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden"
          style={scrollRowStyle}
        >
          {selectedTags.map((tag) => (
            <span
              key={tag}
              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-foreground px-3 py-1 text-sm text-primary-foreground"
            >
              {tag}
              <button
                type="button"
                onClick={() => {
                  onTagsChange(selectedTags.filter((t) => t !== tag));
                  if (conflictWarning?.toLowerCase() === tag.toLowerCase()) {
                    setConflictWarning(null);
                  }
                }}
                className="ml-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Suggested tags — horizontal scroll (hide selected) */}
      <div
        className="mt-3 -mx-1 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden"
        style={scrollRowStyle}
      >
        <div className="flex gap-1.5 px-1" style={{ minWidth: "max-content" }}>
          {suggestedTags
            .filter((tag) => !selectedTags.some((t) => t.toLowerCase() === tag.toLowerCase()))
            .map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                disabled={atLimit}
                className={`whitespace-nowrap rounded-full border px-3 py-1 text-sm transition-colors ${
                  atLimit
                    ? "border-border bg-muted text-muted-foreground opacity-50 cursor-not-allowed"
                    : "border-border bg-card text-foreground hover:border-accent"
                }`}
              >
                {tag}
              </button>
            ))}
        </div>
      </div>

      {/* Custom tag input */}
      <div className="mt-2">
        <input
          ref={inputRef}
          type="text"
          value={customInput}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (customInput.trim()) addCustomTag(); }}
          placeholder="Add your own..."
          disabled={atLimit}
          className="w-full rounded-full border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        />
      </div>

      {/* Word limit warning */}
      {wordWarning && (
        <p className="mt-1 text-xs text-amber-600">Tags are 2 words max</p>
      )}

      {/* Conflict warning */}
      {conflictWarning && conflictLabel && (
        <p className="mt-1 text-xs text-amber-600">
          You've added "{conflictWarning}" to {conflictLabel} — adding it here may confuse the AI.
        </p>
      )}

      {/* Counter */}
      <p className="mt-1 text-xs text-muted-foreground">
        {selectedTags.length} of {maxTags} added
      </p>
    </div>
  );
};

export default TagPickerField;
